import { STAGE_TELEMETRY_URL, PROD_TELEMETRY_URL } from '../../config.js';
import { setCookie, getCookie } from '../../utils/cookie.js';
import { onPageHide } from '../../utils/onPageHide.js';
import { onSessionPaused } from '../../utils/onSessionPaused.js';
import { PlatformAPI, isTouchDevice } from '../../utils/platform.js';
import { uuidv4, validateUUID } from '../../utils/uuid.js';
import { version } from '../../version.js';
import { ElementClicked, PageVisited, PageTranslated, SessionPaused } from './web.schema.js';
import { setUser, setTag } from '../../../node_modules/.pnpm/@sentry_core@8.30.0/node_modules/@sentry/core/build/esm/exports.js';

const urlKeysToReplace = ["token", "hash"];
function generateSessionID() {
    const id = sessionStorage.getItem(TelemetryClient.SESSION_ID_KEY);
    if (id && validateUUID(id)) {
        return id;
    }
    const newID = uuidv4();
    sessionStorage.setItem(TelemetryClient.SESSION_ID_KEY, newID);
    return newID;
}
function getAndResetCookie(name, ttlHours, domain) {
    const cookie = getCookie(name);
    if (cookie && validateUUID(cookie)) {
        setCookie(name, cookie, ttlHours, domain); // Reseting ttl
        return cookie;
    }
    const newCookie = uuidv4();
    setCookie(name, newCookie, ttlHours, domain);
    return newCookie;
}
function getCleanedUrl(location) {
    const search = getCleanedSearchParams(location);
    const hash = getCleanedHashParams(location);
    return `${location.origin}${location.pathname}${search}${hash}`;
}
function getCleanedSearchParams(location) {
    const searchParams = new URLSearchParams(location.search);
    for (const key of urlKeysToReplace) {
        if (searchParams.has(key)) {
            searchParams.set(key, "hidden");
        }
    }
    return searchParams.toString().length ? `?${searchParams.toString()}` : "";
}
function getCleanedHashParams(location) {
    const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
    for (const key of urlKeysToReplace) {
        if (hashParams.has(key)) {
            hashParams.set(key, "hidden");
        }
    }
    return hashParams.toString().length ? `#${hashParams.toString()}` : "";
}
var TelemetryClientEnvironment;
(function (TelemetryClientEnvironment) {
    TelemetryClientEnvironment["PROD"] = "prod";
    TelemetryClientEnvironment["BETA"] = "beta";
    TelemetryClientEnvironment["STAGE"] = "stage";
    TelemetryClientEnvironment["DEV"] = "dev";
})(TelemetryClientEnvironment || (TelemetryClientEnvironment = {}));
class TelemetryClient {
    static TRACKING_ID_COOKIE_NAME = "t_id";
    static SESSION_ID_KEY = "s_id";
    userId;
    clientVersion;
    siteVersion;
    telemetryUrl = STAGE_TELEMETRY_URL;
    appName;
    titleOverride;
    cookieDomain;
    cookiesEnabled;
    experiments = {};
    constructor({ appName, siteVersion, cookieDomainOverride, telemetryUrlOverride, titleOverride, userId, environment, }) {
        if (environment === TelemetryClientEnvironment.PROD) {
            this.telemetryUrl = PROD_TELEMETRY_URL;
        }
        if (telemetryUrlOverride) {
            this.telemetryUrl = telemetryUrlOverride;
        }
        this.titleOverride = titleOverride;
        this.siteVersion = siteVersion;
        this.clientVersion = version;
        this.appName = appName;
        this.cookieDomain =
            environment === TelemetryClientEnvironment.DEV ? "localhost" : "shapr3d.com";
        if (cookieDomainOverride) {
            this.cookieDomain = cookieDomainOverride;
        }
        this.cookiesEnabled = this.isCookiesEnabled();
        this.userId = userId ?? null;
        setUser({ id: userId ?? undefined });
    }
    isCookiesEnabled() {
        const TEST_COOKIE_NAME = "shaprtestcookie";
        setCookie(TEST_COOKIE_NAME, "1", 1, this.cookieDomain);
        const cookie = getCookie(TEST_COOKIE_NAME);
        setCookie(TEST_COOKIE_NAME, "", 0, this.cookieDomain);
        return cookie != null;
    }
    setUserId(userId) {
        this.userId = userId;
        setUser({ id: userId ?? undefined });
    }
    getTrackingID() {
        const trackingID = getAndResetCookie(TelemetryClient.TRACKING_ID_COOKIE_NAME, 24 * 182, this.cookieDomain);
        setTag("trackingID", trackingID);
        return trackingID;
    }
    resetTrackingID() {
        setCookie(TelemetryClient.TRACKING_ID_COOKIE_NAME, "", 0, this.cookieDomain);
        return this.getTrackingID();
    }
    trackTelemetryEvents(telemetryEvents) {
        try {
            const events = telemetryEvents.map((telemetryEvent) => ({
                event_id: uuidv4(),
                app_name: this.appName,
                client_version: this.clientVersion,
                event_name: telemetryEvent.eventName,
                event_properties: telemetryEvent.eventProperties,
                event_time: new Date().toISOString(),
                experiments: this.experiments,
                hash: getCleanedHashParams(window.location),
                page_title: this.titleOverride ?? document.title,
                session_id: generateSessionID(),
                site_version: this.siteVersion,
                tracking_id: this.getTrackingID(),
                url: getCleanedUrl(window.location),
                user_agent: window.navigator.userAgent,
                user_id: this.userId ?? undefined,
                cookies_enabled: this.cookiesEnabled,
                visibility_state: document.visibilityState,
                platform: PlatformAPI.getInstance().getPlatform(),
                os_name: PlatformAPI.getInstance().getOSName(),
                os_version: PlatformAPI.getInstance().getOSVersion(),
                browser_name: PlatformAPI.getInstance().getBrowserName(),
                browser_version: PlatformAPI.getInstance().getBrowserVersion(),
                is_touch_device: isTouchDevice(),
                viewport_width: window.screen.width,
                viewport_height: window.screen.height,
            }));
            this.sendRequest(events);
        }
        catch (err) {
            console.warn(`Couldn't track event ${telemetryEvents}:\n ${err}`);
        }
    }
    trackTelemetryEvent(telemetryEvent) {
        this.trackTelemetryEvents([telemetryEvent]);
    }
    setExperiments(experiments) {
        this.experiments = Object.entries(experiments).reduce((acc, [key, value]) => ({ ...acc, [key]: value?.toString() ?? "" }), {});
    }
    sendRequest(evt) {
        navigator.sendBeacon(this.telemetryUrl, JSON.stringify(evt));
    }
}
class TelemetryUtils {
    client;
    referrer;
    constructor(client, referrer = "") {
        this.client = client;
        this.referrer = referrer;
    }
    processSymbolOverrides(telemetrySelector) {
        const overrideFields = document.querySelectorAll("[data-telemetry-override]");
        overrideFields.forEach((field) => {
            if (field.dataset) {
                const overrideScope = field.parentElement;
                const overrideValue = field.innerHTML;
                const overrideAttribute = field.dataset.telemetryOverride;
                if (overrideScope && overrideAttribute && overrideValue) {
                    const trackedElements = overrideScope.querySelectorAll(telemetrySelector);
                    if (trackedElements) {
                        trackedElements.forEach((element) => {
                            element.dataset[overrideAttribute] = overrideValue;
                        });
                    }
                }
            }
        });
    }
    trackClicks(telemetrySelector) {
        this.processSymbolOverrides(telemetrySelector);
        const elements = document.querySelectorAll(telemetrySelector);
        elements.forEach((element) => {
            element.addEventListener("click", () => {
                this.client.trackTelemetryEvent(new ElementClicked({
                    elementId: element.id,
                    action: element?.dataset?.action,
                    location: element?.dataset?.location,
                    details: element?.dataset?.details,
                }));
            });
        });
    }
    trackPageViews() {
        this.client.trackTelemetryEvent(new PageVisited({ referrer: this.referrer }));
    }
    trackSession() {
        onSessionPaused(({ duration }) => {
            this.client.trackTelemetryEvent(new SessionPaused({ duration }));
        });
    }
    trackMachineTranslation({ defaultLanguage } = { defaultLanguage: "en" }) {
        onPageHide(() => {
            const isPageTranslated = Boolean(document.querySelector("html.translated-ltr, html.translated-rtl, ya-tr-span, *[_msttexthash], *[x-bergamot-translated]"));
            const lang = document.documentElement.lang;
            if (isPageTranslated || lang != defaultLanguage) {
                this.client.trackTelemetryEvent(new PageTranslated({ lang }));
            }
        });
    }
}

export { TelemetryClient, TelemetryClientEnvironment, TelemetryUtils, TelemetryUtils as default, getAndResetCookie };
//# sourceMappingURL=telemetryClient.js.map
